import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fullUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
}

export default function ImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(fullUrl(data.url));
      }
      onChange([...images, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));

  const visibleImages = useMemo(
    () => images.filter(Boolean).map((img, i) => ({ img, i })),
    [images]
  );

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {visibleImages.map(({ img, i }) => (
          <div key={`img-${img}-${i}`} className="relative aspect-[4/3] rounded-xl overflow-hidden group border border-subtle">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`remove-image-${i}`}
              onClick={() => removeAt(i)}
              className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <label
          data-testid="image-upload-input"
          className={`aspect-[4/3] rounded-xl border-2 border-dashed border-subtle flex flex-col items-center justify-center cursor-pointer hover:border-moss hover:bg-moss/5 transition-colors ${uploading ? "opacity-50" : ""}`}
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-moss" />
          ) : (
            <>
              <Upload className="h-5 w-5 text-muted2 mb-1" strokeWidth={1.5} />
              <span className="text-xs text-muted2">Upload</span>
            </>
          )}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}
